import React from 'react'
import '../styles/article.css'

const Chapter3 = () => {
    return (
        <div className='history-page'>
            <h1 className='history-chapter-lbl'>Chapter 3</h1>
            <h1 className='history-heading'>The Emerging Swing Choir in American High Schools</h1>

            <div className='paragraphwimage-xl'>
                <div className='imagewborder'>
                    <img src='images/history/8.jpg' />
                </div>
                <p> The postwar era of
                    the late 1940s and
                    early 1950s was a
                    time of innovation,
                    optimism, and cultural
                    shift in the United
                    States, and these
                    changes swept into high schools across the nation. As families
                    gathered around their radios and television sets, one voice
                    stood out, reverberating through both urban and rural
                    America: Fred Waring, the popular bandleader and choral
                    director who brought choral music to the masses. Waring’s
                    live broadcasts, featuring his choir, The Pennsylvanians,
                    became a significant cultural touchstone. His influence,
                    though perhaps most palpable in urban centers, trickled into
                    rural high schools, sparking a new kind of choral group, the
                    Swing Choir.</p>

            </div>

            <p> The word "swing" itself was borrowed from the big band era of
                the 1940s, when energetic rhythms, dynamic brass sections,
                and syncopated beats dominated American music. But for
                high schools in America, “swing” took on a new
                dimension—one that paired choral singing with popular music
                styles and light choreography. These “Swing Choirs,” as they
                became known, emerged not in bustling cities but in rural high
                schools, where communities seemed ready to embrace this
                new form of entertainment. Surprisingly, two of the earliest
                high school pop ensembles did not appear in metropolitan
                areas like New York or Chicago, where swing and pop music
                pulsed through the streets. Instead, they began in Manhattan,
                Kansas, under the direction of Larry Boye, and LaCrosse,
                Wisconsin, under the direction of Alvin Mikelson. These two
                directors, each in their own way, sparked the Swing Choir
                movement, bringing choral music a step closer to the
                energetic, visually engaging performances that would later
                define the show choir.</p>

            <h2 className='sub-head'>The Influence of Fred Waring</h2>

            <p> Alvin Mikelson’s Swing Choir at LaCrosse Central High School
                drew a direct line to Fred Waring’s influence. Waring’s
                arrangements brought a richness to popular and traditional
                songs alike, weaving together harmony and narrative in ways
                that resonated with audiences. Mikelson’s group quickly
                began incorporating these arrangements, and in the 1949
                LaCrosse Central High School yearbook, The Booster, a
                photograph captures the robed singers of the Swing Choir in
                their debut performance at the school’s Christmas assembly.
                The caption proudly states, “singing Waring’s arrangement of
                the ‘Song of Christmas,’ the newly formed Swing Choir made
                its first appearance.”</p>

            <p> The following years would see even more nods to Waring’s
                style. By 1951, the Swing Choir at LaCrosse had transitioned
                from traditional robes to more contemporary attire, their
                repertoire expanding to include songs like “Old Rockin’ Chair,”
                “Give Me Your Tired, Your Poor,” and “You’ll Never Walk
                Alone.” Waring’s arrangements were at the heart of these
                performances, infusing the choir’s shows with a sense of
                professionalism and polish that elevated their presentations
                beyond the traditional school chorus. Their song choices
                spoke to the era’s longing for patriotism, nostalgia, and unity,
                while lighthearted tunes like “Mary Had a Little Lamb” and
                “The Happy Farmer” balanced the repertoire with a dose of
                playful rhythm.</p>


            <h2 className='sub-head'> A Different Approach in Kansas: Larry Boye's Swing
                Choir</h2>


            <p> While Mikelson embraced Waring’s influence, Larry Boye’s
                approach at Manhattan High School in Kansas took a different
                route. When asked if Waring had inspired his Swing Choir,
                Boye firmly denied it. “Not from Fred Waring. I had heard of
                Fred Waring. We didn’t have television...I just did it,” he
                recalled. Boye’s vision came from a need to captivate his
                students, to give them something that felt fresh and alive, a
                break from the conventional choral performances they’d
                grown accustomed to. “I really just needed something to do
                with my students that would keep their interest...so we put
                together singing and dancing shows...and the kids responded
                to it.”</p>
            <p> Boye, however, would later attend a Waring Summer Choral
                Workshop in the early 1950s, hoping to pick up new ideas for
                his group. Yet he found himself underwhelmed. The
                choreography felt restrained, the staging uninspired. Boye
                wanted to see his students doing more than simply moving
                from picture to picture, standing in neat rows with carefully
                coordinated gestures. “I wanted to see more action, more
                visual excitement...the kids and audiences wanted to have
                more going on,” he explained. This desire for movement and
                spectacle would become a cornerstone of his Swing Choir’s
                identity, setting it apart as one of the earliest groups to
                incorporate true choreography and a theatrical approach to
                performance.</p>

            <h2 className='sub-head'>The Early Repertoire of Swing Choirs</h2>
            <p> The Swing Choirs of this era tapped into popular songs and
                patriotic anthems, creating a setlist that was both timely and
                timeless. Students sang numbers that ranged from American
                classics to folk songs, patriotic tunes, and spirited, rhythmic
                pieces. The repertoire reflected the cultural mood of postwar
                America, celebrating resilience, unity, and hope. Waring’s
                arrangements, which emphasized clear diction, harmonious
                blends, and dramatic expression, found a natural home in
                these choirs. Songs like “Land of Degradation” and “You’ll
                Never Walk Alone” touched on themes of empathy, aspiration,
                and perseverance, while lighter songs kept audiences
                entertained and engaged.</p>



            <h2 className='sub-head'>Swing Choirs as a Cultural Phenomenon</h2>
            <p>The emergence of Swing Choirs in these Midwestern schools
                was more than a musical trend; it marked the beginning of a
                cultural shift in American high schools. By blending popular
                music with choral singing and movement, Swing Choirs
                opened the door to a new kind of performance that was
                interactive, expressive, and accessible. They became a form
                of community entertainment, a source of pride for students
                and audiences alike. And while directors like Mikelson leaned
                on Waring’s established arrangements, others like Boye
                forged their own path, adding their unique flair to the Swing
                Choir tradition.</p>
            <p> These early Swing Choirs laid the groundwork for what would
                eventually evolve into the show choirs we know today. They
                set the stage for a future where high school students would
                not only sing but also dance, act, and perform with an energy
                that bridged the worlds of pop culture and traditional choral
                music. The Swing Choirs of Manhattan and LaCrosse were
                among the first sparks, kindling a movement that would grow
                to captivate schools across America, transforming high school
                choir from a simple extracurricular into a full-fledged cultural
                phenomenon.</p>



        </div>
    )
}

export default Chapter3